// ---------------------------------------------------------------------------
// dragDropFile — utilitarios puros para drag & drop de arquivos (Inputs V3).
// ---------------------------------------------------------------------------
// Centraliza a deteccao "estou arrastando arquivo(s)?" e a extracao do
// File de um ``DataTransfer``, com as regras de bloqueio acordadas:
//
//   - pastas (diretorios) sao rejeitadas;
//   - objetos que nao sao arquivos (texto, links, imagens de outra
//     pagina arrastadas como ``string``) sao rejeitados;
//   - validacao de TIPO de arquivo (extensao/mime) NAO acontece aqui —
//     fica a cargo do backend (decisao do produto).
//
// Tudo sincrono: ``extractDroppedFile`` precisa ler o ``DataTransfer``
// ainda dentro do handler de ``drop`` (depois disso o browser pode
// esvaziar ``items``/``files``). ``webkitGetAsEntry`` e sincrono e
// suportado por Chromium/Firefox/WebKit modernos.
// ---------------------------------------------------------------------------

// Durante ``dragenter``/``dragover`` o ``DataTransfer.types`` ja contem
// "Files" quando o que esta sendo arrastado vem do sistema operacional.
// Selecoes de texto/links populam outros tipos ("text/plain",
// "text/uri-list", etc.) — assim distinguimos arquivo de "outros
// objetos" antes mesmo do drop.
export function dragHasFiles(eventOrDataTransfer) {
  const dt = eventOrDataTransfer?.dataTransfer || eventOrDataTransfer;
  if (!dt || !dt.types) return false;
  // ``types`` pode ser DOMStringList (sem .includes) — normaliza.
  const types = Array.from(dt.types);
  return types.includes("Files");
}

// Retorna ``{ file, error }``. ``error`` e null em caso de sucesso, ou
// uma das chaves: "folder" (pasta), "not_file" (objeto que nao e
// arquivo), "empty" (drop vazio). Em multi-drop pega o PRIMEIRO arquivo
// valido (o fluxo de upload e single-file).
export function extractDroppedFile(dataTransfer) {
  if (!dataTransfer) return { file: null, error: "empty" };

  const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];

  // Deteccao de pasta + "nao-arquivo" via ``items`` quando disponivel:
  //   - kind !== "file"  -> texto/link/etc (objeto que nao e arquivo).
  //   - entry.isDirectory -> pasta.
  if (items.length > 0) {
    let sawFileKind = false;
    for (const it of items) {
      if (it.kind !== "file") continue;
      sawFileKind = true;
      const entry =
        typeof it.webkitGetAsEntry === "function"
          ? it.webkitGetAsEntry()
          : null;
      if (entry && entry.isDirectory) {
        return { file: null, error: "folder" };
      }
    }
    if (!sawFileKind) {
      // Havia itens, mas nenhum do tipo "file" (ex.: arrastou um link).
      return { file: null, error: "not_file" };
    }
  }

  const files = dataTransfer.files ? Array.from(dataTransfer.files) : [];
  if (files.length === 0) {
    // Sem ``items`` confiaveis e sem ``files`` -> nada utilizavel.
    return { file: null, error: items.length > 0 ? "not_file" : "empty" };
  }

  // Heuristica extra p/ browsers sem ``webkitGetAsEntry``: uma pasta
  // solta costuma chegar como File com ``type`` vazio E ``size`` 0.
  // Como nao da pra confirmar com certeza, so aplicamos quando NAO
  // tivemos ``items`` para inspecionar acima.
  const first = files[0];
  if (items.length === 0 && first && first.type === "" && first.size === 0) {
    return { file: null, error: "folder" };
  }

  return { file: first, error: null };
}
